import React from 'react';
import { 
  FileText, 
  BookOpen, 
  HelpCircle, 
  Wallet, 
  TrendingUp, 
  Gift, 
  Shield, 
  Zap,
  ExternalLink,
  ChevronRight
} from 'lucide-react';
import BackButton from '../components/BackButton';

export default function Documentation() {
  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Zap,
      items: [
        {
          title: 'What is Plypto?',
          content: 'Plypto is a decentralized prediction platform where users can make predictions on cryptocurrency price movements and earn PLYP tokens as rewards.'
        },
        {
          title: 'How to Connect Your Wallet',
          content: 'Click the "Connect Wallet" button in the top navigation. We support MetaMask, WalletConnect, and other popular Web3 wallets.'
        },
        {
          title: 'Making Your First Prediction',
          content: 'Browse the available markets on the homepage, select a cryptocurrency pair, choose your prediction direction (up/down), set your amount, and confirm the transaction.'
        }
      ]
    },
    {
      id: 'predictions',
      title: 'Making Predictions',
      icon: TrendingUp,
      items: [
        {
          title: 'How Predictions Work',
          content: 'Predictions are time-based bets on cryptocurrency price movements. You predict whether a price will go up or down within a specific timeframe.'
        },
        {
          title: 'Prediction Types',
          content: 'We offer various prediction timeframes: 5 minutes, 15 minutes, 30 minutes, 1 hour, and 4 hours. Shorter timeframes offer higher risk and reward.'
        },
        {
          title: 'Calculating Winnings',
          content: 'Winnings are calculated based on the prediction amount, market volatility, and the accuracy of your prediction. Successful predictions earn both your stake back plus additional PLYP tokens.'
        }
      ]
    },
    {
      id: 'plyp-tokens',
      title: 'PLYP Tokens',
      icon: Gift,
      items: [
        {
          title: 'What are PLYP Tokens?',
          content: 'PLYP is the native utility token of the Plypto platform. It can be earned through successful predictions, daily activities, and special promotions.'
        },
        {
          title: 'How to Earn PLYP',
          content: 'Earn PLYP through: successful predictions (5-50 PLYP), daily login rewards (10 PLYP), weekly challenges, and referral bonuses (25 PLYP per referral).'
        },
        {
          title: 'Using PLYP Tokens',
          content: 'PLYP tokens can be used for premium features, higher prediction limits, exclusive markets, and can be traded on supported decentralized exchanges.'
        }
      ]
    },
    {
      id: 'wallet-security',
      title: 'Wallet & Security',
      icon: Shield,
      items: [
        {
          title: 'Wallet Security',
          content: 'Always keep your wallet seed phrase secure and never share it with anyone. Use hardware wallets for large amounts and enable two-factor authentication where possible.'
        },
        {
          title: 'Transaction Safety',
          content: 'Always verify transaction details before confirming. Check the recipient address, amount, and gas fees. Transactions on the blockchain are irreversible.'
        },
        {
          title: 'Smart Contract Security',
          content: 'Our smart contracts are audited and open-source. However, DeFi involves risks. Only invest what you can afford to lose and understand the risks involved.'
        }
      ]
    }
  ];

  const faqs = [
    {
      question: 'What cryptocurrencies can I predict on?',
      answer: 'We support major cryptocurrencies including Bitcoin (BTC), Ethereum (ETH), Solana (SOL), Cardano (ADA), and many others. New markets are added regularly.'
    },
    {
      question: 'What are the fees?',
      answer: 'Plypto charges a small platform fee (typically 2-5%) on winning predictions. Gas fees for blockchain transactions are separate and depend on network congestion.'
    },
    {
      question: 'How long do predictions take to resolve?',
      answer: 'Predictions resolve automatically at the end of their timeframe. Results are typically available within 1-2 minutes after the prediction period ends.'
    },
    {
      question: 'Can I cancel a prediction?',
      answer: 'Once a prediction is confirmed on the blockchain, it cannot be cancelled. Make sure to review all details before confirming your transaction.'
    },
    {
      question: 'Is Plypto available worldwide?',
      answer: 'Plypto is available globally, but users should comply with their local regulations regarding cryptocurrency and prediction markets.'
    }
  ];

  return (
    <div className="bg-plypto-bg min-h-screen">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <BackButton />
            <div className="h-6 w-px bg-white/20"></div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Documentation</h1>
                <p className="text-gray-400">Learn how to use Plypto and maximize your trading potential</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          
          {/* Table of Contents */}
          <div className="lg:col-span-1">
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 sticky top-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <BookOpen className="w-5 h-5 text-blue-400" />
                <span>Table of Contents</span>
              </h3>
              
              <nav className="space-y-2">
                {sections.map(section => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors py-2 px-3 rounded-lg hover:bg-white/10"
                  >
                    <section.icon className="w-4 h-4" />
                    <span className="text-sm">{section.title}</span>
                  </a>
                ))}
                <a
                  href="#faq"
                  className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors py-2 px-3 rounded-lg hover:bg-white/10"
                >
                  <HelpCircle className="w-4 h-4" />
                  <span className="text-sm">FAQ</span>
                </a>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">
            
            {/* Documentation Sections */}
            {sections.map(section => (
              <section key={section.id} id={section.id} className="bg-white/5 rounded-xl p-8 border border-white/10">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">{section.title}</h2>
                </div>
                
                <div className="space-y-6">
                  {section.items.map((item, index) => (
                    <div key={index} className="bg-white/5 rounded-lg p-6 border border-white/10">
                      <h3 className="text-lg font-semibold text-white mb-3 flex items-center space-x-2">
                        <ChevronRight className="w-4 h-4 text-blue-400" />
                        <span>{item.title}</span>
                      </h3>
                      <p className="text-gray-300 leading-relaxed">{item.content}</p>
                    </div>
                  ))}
                </div>
              </section>
            ))}

            {/* FAQ Section */}
            <section id="faq" className="bg-white/5 rounded-xl p-8 border border-white/10">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">Frequently Asked Questions</h2>
              </div>
              
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-white/5 rounded-lg p-6 border border-white/10">
                    <h3 className="text-lg font-semibold text-white mb-3">{faq.question}</h3>
                    <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Support */}
            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl p-8 border border-blue-500/20">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-2">Need More Help?</h3>
                <p className="text-gray-400 mb-6">
                  Can't find what you're looking for? Our support team is here to help.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors">
                    <ExternalLink className="w-4 h-4" />
                    <span>Contact Support</span>
                  </button>
                  <button className="flex items-center justify-center space-x-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg transition-colors border border-white/20">
                    <ExternalLink className="w-4 h-4" />
                    <span>Join Discord</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
